const FINAL_STATUSES = ['completed', 'failed', 'canceled'];

// Entries from the WebSocket carry the run id; an entry without one matches
// the run's exact version, which is unique per task.
function isSameRun(entry, runId, version) {
    if (entry.distillation_event_id) return entry.distillation_event_id === runId;
    return version != null && Number(entry.version) === Number(version);
}

/**
 * Gives the run an `evaluation` response reports finished the same final
 * status in `evaluationProgress`. The WebSocket's final update for it may
 * have been published while the socket was down, and nothing else would end
 * the in-progress entries. With `addMissing`, a run that has no entry at all
 * (every update for it was missed) gets the entry its final update would have
 * created. Returns `progress` itself when nothing changes.
 */
export function finishEvaluationProgress(progress, usecaseId, evaluation, { addMissing = false } = {}) {
    const status = evaluation?.distillation_status;
    const runId = evaluation?.selected_distillation_event_id;
    const version = evaluation?.selected_version;
    if (!FINAL_STATUSES.includes(status) || !runId) return progress;
    let next = progress;
    let hasEntry = false;
    for (const [key, entry] of Object.entries(progress || {})) {
        if (entry?.usecase_id !== usecaseId || !isSameRun(entry, runId, version)) continue;
        hasEntry = true;
        if (FINAL_STATUSES.includes(entry.status)) continue;
        if (next === progress) next = { ...progress };
        next[key] = { ...entry, status, error_details: evaluation.distillation_error_details ?? entry.error_details };
    }
    if (hasEntry || !addMissing || version == null) return next;
    return {
        ...progress,
        [`${usecaseId}_${version}`]: {
            usecase_id: usecaseId,
            distillation_event_id: runId,
            version,
            status,
            error_details: evaluation.distillation_error_details,
            progress: evaluation.initial_progress,
            step_name: evaluation.initial_step_name,
            current_step: evaluation.initial_current_step,
            total_steps: evaluation.initial_total_steps,
        },
    };
}

/**
 * Runs of a task still in flight locally, once each: the run of every
 * progress entry that is not final, plus `pendingRun` ({ runId, version }),
 * the recalculation a page started and may have no entry for yet.
 */
export function inFlightEvaluationRuns(progress, usecaseId, pendingRun) {
    const runs = new Map();
    if (pendingRun?.runId) runs.set(pendingRun.runId, pendingRun);
    for (const entry of Object.values(progress || {})) {
        const runId = entry?.distillation_event_id;
        if (entry?.usecase_id !== usecaseId || !runId || FINAL_STATUSES.includes(entry.status)) continue;
        if (!runs.has(runId)) runs.set(runId, { runId, version: entry.version });
    }
    return [...runs.values()];
}

/** The run a successful `recalculate-evaluation` response started, or null. */
export function recalculationRunFromResponse(response) {
    const runId = response?.distillation_event_id;
    return runId ? { runId, version: response.new_version } : null;
}
